import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Image as ImageIcon, Video } from "lucide-react";
import { toast } from "react-hot-toast";
import { storageService } from "@/services/storageService";
import type { EntityType } from "@/services/storageService";
import type { StorageUploadProgress } from "@/lib/storageUpload";
import { getStorageUploadErrorMessage } from "@/lib/storageUpload";
import { FILE_ACCEPT } from "@/constants/fileAccept";
import {
  FILE_SIZE_BYTES,
  FILE_VALIDATION_PRESETS,
  formatFileSizeLimit,
  getFileKind,
  partitionValidatedFiles,
  toastRejectedFiles,
} from "@/lib/fileValidation";
import { cn } from "@/lib/utils";
import { needsAuthenticatedStorageFetch } from "@/lib/storageFetch";
import { useImageCropSession } from "@/hooks/useImageCropSession";
import { useStorageObjectUrl } from "@/hooks/useStorageObjectUrl";
import { Label } from "@/components/ui";
import { UploadProgressOverlay } from "@/components/common/UploadProgressOverlay";
import { ImageCropModal } from "@/components/common/ImageCropModal";
import { FileDropzone } from "./FileDropzone";

export type MediaType = "image" | "video" | "any";

export interface MediaUploadCropOptions {
  /** Crop frame width / height (e.g. 16/9 for carousel banners). */
  aspect: number;
  description: string;
  /** Optional CSS class on the crop hole (e.g. storefront 4:3 guide). */
  cropAreaClassName?: string;
}

export interface MediaUploadProps {
  /** Current media URL */
  value?: string;
  /** Callback when media changes */
  onChange: (url: string | undefined) => void;
  /** Entity type for storage path */
  entityType: EntityType;
  /** Entity ID for storage path */
  entityId: string;
  /** Type of media to accept */
  mediaType?: MediaType;
  /** Label displayed above the upload area */
  label?: string;
  /** Hint text displayed below */
  hint?: string;
  /** Custom class for the container */
  className?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Preview frame preset. For a numeric ratio use `crop.aspect` instead. */
  aspectRatio?: "square" | "video" | "auto";
  /** Whether the component is disabled */
  disabled?: boolean;
  /** When set, images are cropped in a modal before upload. */
  crop?: MediaUploadCropOptions;
  /**
   * When true (default), removing the preview also deletes the file from storage.
   * Set false for forms that should only clear local state until save.
   */
  deleteFileOnRemove?: boolean;
}

const sizeConfig = {
  sm: { container: "h-20 w-20", icon: "h-6 w-6", text: "text-xs" },
  md: { container: "h-32 w-32", icon: "h-8 w-8", text: "text-xs" },
  lg: { container: "h-48 w-48", icon: "h-12 w-12", text: "text-sm" },
};

const aspectRatioPresetClass = {
  square: "aspect-square",
  video: "aspect-video",
  auto: "",
} as const;

export function MediaUpload({
  value,
  onChange,
  entityType,
  entityId,
  mediaType = "image",
  label,
  hint,
  className,
  size = "md",
  aspectRatio = "square",
  disabled = false,
  crop,
  deleteFileOnRemove = true,
}: MediaUploadProps) {
  const { t } = useTranslation("components");
  const { t: tCommon } = useTranslation("common");
  const inputId = useId();
  const [uploadProgress, setUploadProgress] = useState<StorageUploadProgress | null>(
    null,
  );
  const isUploading = uploadProgress !== null;
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(value);
  const [pendingLocalPreview, setPendingLocalPreview] = useState<
    string | undefined
  >();
  const [previewType, setPreviewType] = useState<"image" | "video">("image");
  const {
    session: cropSession,
    requestImageCrop,
    resolveCrop,
  } = useImageCropSession();

  const remote = useStorageObjectUrl(
    previewUrl && needsAuthenticatedStorageFetch(previewUrl)
      ? previewUrl
      : undefined,
  );
  const remoteSrc = remote.status === "ready" ? remote.url : undefined;
  const displaySrc =
    !previewUrl
      ? undefined
      : needsAuthenticatedStorageFetch(previewUrl)
        ? remoteSrc ?? pendingLocalPreview
        : previewUrl;
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync previewUrl with value prop (for when parent data changes)
  useEffect(() => {
    setPreviewUrl(value);
  }, [value]);

  useEffect(() => {
    if (!pendingLocalPreview || !previewUrl) return;
    if (!needsAuthenticatedStorageFetch(previewUrl)) {
      URL.revokeObjectURL(pendingLocalPreview);
      setPendingLocalPreview(undefined);
      return;
    }
    if (remoteSrc) {
      URL.revokeObjectURL(pendingLocalPreview);
      setPendingLocalPreview(undefined);
    }
  }, [previewUrl, remoteSrc, pendingLocalPreview]);

  useEffect(() => {
    return () => {
      if (pendingLocalPreview) {
        URL.revokeObjectURL(pendingLocalPreview);
      }
    };
  }, [pendingLocalPreview]);

  const acceptMap = {
    image: FILE_ACCEPT.image,
    video: FILE_ACCEPT.video,
    any: FILE_ACCEPT.imageAndVideo,
  } as const;

  const uploadFile = async (file: File) => {
    const validationOptions =
      mediaType === "image"
        ? {
            kinds: FILE_VALIDATION_PRESETS.catalogImage.kinds,
            maxSize: FILE_VALIDATION_PRESETS.catalogImage.maxSize,
          }
        : mediaType === "video"
          ? {
              kinds: ["video"] as const,
              maxSize: FILE_SIZE_BYTES.VIDEO,
            }
          : FILE_VALIDATION_PRESETS.activityMedia;

    const { accepted, rejected } = partitionValidatedFiles(
      [file],
      validationOptions,
    );
    toastRejectedFiles(rejected, {
      tooLarge: (_file, maxSize) =>
        t("mediaUploadMax", { max: formatFileSizeLimit(maxSize) }),
      unsupported: () => {
        if (mediaType === "image") {
          return t("mediaUploadImage");
        }
        if (mediaType === "video") {
          return t("mediaUploadVideo");
        }
        return t("mediaUploadEither");
      },
    });
    if (accepted.length === 0) {
      return;
    }

    let selectedFile = accepted[0].file;
    const isVideo = getFileKind(selectedFile) === "video";

    if (crop && !isVideo) {
      const cropped = await requestImageCrop(selectedFile);
      if (!cropped || !isMountedRef.current) {
        return;
      }
      selectedFile = cropped;
    }

    const localPreview = URL.createObjectURL(selectedFile);
    setPendingLocalPreview(localPreview);
    setPreviewUrl(localPreview);
    setPreviewType(isVideo ? "video" : "image");
    setUploadProgress({ progress: 0, phase: "uploading" });

    try {
      const publicUrl = await storageService.uploadFile(
        entityType,
        entityId,
        selectedFile,
        { onProgress: setUploadProgress },
      );
      if (!isMountedRef.current) {
        return;
      }
      onChange(publicUrl);
      setPreviewUrl(publicUrl);
      toast.success(
        t("mediaUploadSuccess", {
          type: isVideo ? t("mediaTypeVideo") : t("mediaTypeImage"),
        }),
      );
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      console.error("Upload failed:", error);
      toast.error(getStorageUploadErrorMessage(error, t));
      setPreviewUrl(value);
      URL.revokeObjectURL(localPreview);
      setPendingLocalPreview(undefined);
    } finally {
      if (isMountedRef.current) {
        setUploadProgress(null);
      }
    }
  };

  const handleRemove = async () => {
    if (value && deleteFileOnRemove) {
      try {
        await storageService.deleteFile(value);
      } catch (error) {
        console.error("Failed to delete file:", error);
      }
    }
    if (pendingLocalPreview) {
      URL.revokeObjectURL(pendingLocalPreview);
      setPendingLocalPreview(undefined);
    }
    onChange(undefined);
    setPreviewUrl(undefined);
  };

  const { container, icon, text } = sizeConfig[size];
  const PlaceholderIcon = mediaType === "video" ? Video : ImageIcon;
  const numericAspect = crop?.aspect;
  const isWidePreview = numericAspect != null || aspectRatio === "video";
  const previewContainerClass = numericAspect
    ? "w-full max-w-xl"
    : aspectRatio === "video"
      ? cn("w-full max-w-md", aspectRatioPresetClass.video)
      : cn(container, aspectRatioPresetClass[aspectRatio]);
  const previewContainerStyle =
    numericAspect != null ? { aspectRatio: String(numericAspect) } : undefined;

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={inputId} spacing="field">
          {label}
        </Label>
      )}

      {previewUrl ? (
        <div
          className={cn(
            "relative inline-block",
            isWidePreview && "w-full max-w-xl",
          )}
        >
          <div
            className={cn(
              "overflow-hidden rounded-lg border border-surface-200",
              previewContainerClass,
            )}
            style={previewContainerStyle}
          >
            {previewType === "video" ? (
              <video
                src={displaySrc}
                className="h-full w-full object-cover"
                muted
              />
            ) : (
              <img
                src={displaySrc}
                alt={t("productItem.previewAlt")}
                className="h-full w-full object-cover"
              />
            )}
            {uploadProgress ? (
              <UploadProgressOverlay upload={uploadProgress} />
            ) : null}
          </div>
          {!isUploading && !disabled ? (
            <button
              type="button"
              onClick={() => void handleRemove()}
              aria-label={tCommon("delete")}
              title={tCommon("delete")}
              className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-md bg-black/55 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-error-fill"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : (
        <FileDropzone
          id={inputId}
          accept={acceptMap[mediaType]}
          disabled={disabled}
          isLoading={isUploading}
          className={cn(
            "gap-2 px-4 py-4",
            previewContainerClass,
            disabled
              ? "cursor-not-allowed border-surface-200 bg-surface-50"
              : undefined,
          )}
          style={previewContainerStyle}
          icon={PlaceholderIcon}
          iconClassName={icon}
          title={
            <span className={cn("text-surface-500", text)}>
              {t("mediaUploadButton")}
            </span>
          }
          titleClassName="font-normal"
          onFilesSelected={(files) => {
            const file = files[0];
            if (file) {
              void uploadFile(file);
            }
          }}
        />
      )}

      {hint && <p className="text-xs text-surface-500">{hint}</p>}

      {crop ? (
        <ImageCropModal
          session={cropSession}
          aspect={crop.aspect}
          cropAreaClassName={crop.cropAreaClassName}
          labels={{
            title: t("imageCrop.title"),
            description: crop.description,
            confirm: t("imageCrop.confirm"),
            cancel: t("imageCrop.cancel"),
            zoom: t("imageCrop.zoom"),
            failed: t("imageCrop.failed"),
          }}
          onConfirm={resolveCrop}
          onCancel={() => resolveCrop(null)}
        />
      ) : null}
    </div>
  );
}
